import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { isMobile } from 'react-device-detect';
import Card from '../components/Ui/Card.jsx';
import BarChart from "../components/Ui/BarChart.jsx";
import { fetchDashboard, fetchHomeAlerts, fetchLinkedAccounts } from "../http/account.js";
import { Alert, CarrosselAlert } from "../components/Ui/Alert.jsx";
import { InputContainer, StyledSelect, Label } from "../components/StyledComponents/Table/TableComponents.jsx";
import { CancelButton } from "../components/StyledComponents/Buttons/Buttons.jsx";
import { GetStateOfDay } from "../components/Helpers/dateFns.js";
import { opticians } from "../components/Helpers/userTypes.js";

const CenterContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 100vh;
  width: 100%;
`;

const FormContainer = styled.div`
  border-radius: 5px;
  background-color: #fff;
  width: ${isMobile ? '100%' : '50%'};
`;

const Heading = styled.h3`
  margin: 0;
  margin-top: 10px;
  padding-bottom: 5px;
`;

const Paragraph = styled.p`
  margin: 0 0 20px 0;
  font-size: 15px;
`;

const BackButton = styled.button`
  margin-top: 20px;
  padding: 10px 20px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  &:hover {
    background-color: #0056b3;
  }
`;

export default function Home({ colour, accountType }) {
  const [dashboard, setDashboard] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [detailedData, setDetailedData] = useState(null);
  const [viewingDetail, setViewingDetail] = useState(false);
  const [messages, setMessages] = useState([]);
  const [selectedOrderType, setSelectedOrderType] = useState('EyeTicket Orders');

  const navigate = useNavigate();

  function navigateToEyeTicketHistory(status) {
    if (selectedAccount) {
      navigate(`/tracking?tStatus=${status}&cAccount=${selectedAccount}`);
    }
  }

  useEffect(() => {
    const loadMessages = async () => {
      setIsLoading(true);
      try {
        const messages = await fetchHomeAlerts();
        setMessages(messages);
        setError(null);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    };

    const loadLinkedAccounts = async () => {
      setIsLoading(true);
      try {
        const accounts = await fetchLinkedAccounts();
        const userAccounts = accounts.map(account => ({
          label: account.accountdescrip,
          value: account.accountid
        }));

        setAccounts(userAccounts);
        if (userAccounts.length > 0) {
          setSelectedAccount(userAccounts[0].value);
        }
        setError(null);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    };

    loadLinkedAccounts();
    loadMessages();
  }, []);

  const loadDashboard = useCallback(async (account) => {
    setIsLoading(true);
    try {
      const fetchedDashboard = await fetchDashboard(account, accountType);
      let dataToProcess;

      if (opticians.includes(accountType)) {

        const selectedOrder = fetchedDashboard.find(order => order.OrderTypes === selectedOrderType);
        dataToProcess = selectedOrder?.Stats[0];
      } else {
        dataToProcess = fetchedDashboard[0];
      }

      if (!dataToProcess) {
        setDashboard({});
        return;
      }

      const consolidatedData = dataToProcess.StatusSummary.reduce((acc, item) => {
        acc[item.Status] = (acc[item.Status] || 0) + item.StatusCount;
        return acc;
      }, {});

      setDashboard({
        summary: dataToProcess.StatusSummary,
        consolidated: consolidatedData
      });
      setDetailedData(null);
      setViewingDetail(false);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [accountType, selectedOrderType]);

  useEffect(() => {
    if (selectedAccount) {
      loadDashboard(selectedAccount);
    }
  }, [selectedAccount, loadDashboard]);

  const handleAccountChange = (e) => {
    setSelectedAccount(e.target.value);
  };

  const handleBarClick = (status) => {
    const detailedData = dashboard.summary?.filter(item => item.Status === status);
    setDetailedData(detailedData);
    setViewingDetail(true);
  };

  const handleBackClick = () => {
    setViewingDetail(false);
  };

  return (
    <CenterContainer>
      <FormContainer>
        <Card
          label={`${colour.appName} - Home`}
          headercolour={colour.sidebar}
          headerTextColour={colour.font}>
          {isLoading && <Alert type='info'>Loading Dashboard...</Alert>}
          {error && !isLoading && <Alert type='error'>An error occurred whilst loading the dashboard: {error.message}</Alert>}
          {!isLoading && !error && (
            <div>
              <Heading>Good {GetStateOfDay()} {accounts.find(acc => acc.value === selectedAccount)?.label}</Heading>
              <Paragraph>To quickly navigate to your tickets, click on the chart below.</Paragraph>
              <BarChart
                data={viewingDetail ? detailedData : dashboard.consolidated}
                selectedBarFn={handleBarClick}
                isDetailView={viewingDetail}
              >
                
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <InputContainer key='tblAccountFilters'>
                      <Label htmlFor='tblAccountFilters'>Account: </Label>
                      <StyledSelect
                        name='tblAccountFilters'
                        id='tblAccountFilters'
                        value={selectedAccount || ""}
                        onChange={handleAccountChange}
                      >
                        {accounts.map((option, index) => (
                          <option key={index} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </StyledSelect>
                      </InputContainer>
                    {opticians.includes(accountType) && (
                        <InputContainer key='tblAccountFilters'>
                        <Label htmlFor='orderTypeFilter'>Order Type: </Label>
                        <StyledSelect
                          name='orderTypeFilter'
                          id='orderTypeFilter'
                          value={selectedOrderType}
                          onChange={(e) => setSelectedOrderType(e.target.value)}
                        >
                          <option value="EyeTicket Orders">EyeTicket Orders</option>
                          <option value="Private Orders">Private Orders</option>                     
                        </StyledSelect>
                        </InputContainer>
                    )}
                  </div>
                
                {viewingDetail && (
                  <CancelButton onClick={handleBackClick} style={{ float: 'right', marginTop: '5px' }}>Back</CancelButton>
                )}
              </BarChart>
              {messages &&
                <CarrosselAlert type="info" arrAlerts={messages} />
              }
            </div>
          )}
        </Card>
      </FormContainer>
    </CenterContainer>
  );
}
